<template>
  <div class="number-stepper">
    <button
      :class="{ disabled: isDecreaseButtonDisabled }"
      class="stepper-button decr primary"
      @click.prevent="doStep('decrease')"
    >
      <span>-</span>
    </button>
    <input
      v-model="currentValue"
      type="number"
      :name="name"
      :disabled="disabled"
      pattern="[0-9]"
      class="stepper-input"
      step="1"
      @input="onInputChange"
    />
    <button
      :class="{ disabled: isIncreaseButtonDisabled }"
      class="stepper-button incr primary"
      @click.prevent="doStep('increase')"
    >
      <span>+</span>
    </button>
  </div>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

/* eslint-disable @typescript-eslint/no-unsafe-member-access */

/* TODO: Add proper typing to resolve type safety warnings */

import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'

@Component({
  emits: [
    'update:ModelValue',
    'input',
    'change',
    'changeType',
    'increase',
    'decrease',
  ],
})
class NumberStepper extends Vue {
  @Prop({ required: true, type: Number })
  modelValue: number

  @Prop({ required: false, type: Boolean, default: false })
  disabled: boolean

  @Prop({ required: false, type: String })
  name: string

  @Prop({ required: false, type: Number, default: 1 })
  min: number

  @Prop({ required: false, type: Number, default: Infinity })
  max: number

  @Prop({ required: false, type: Number, default: 1 })
  defaultValue: number

  currentValue: number = 0

  beforeMount() {
    let value = this.modelValue ? +this.modelValue : +this.defaultValue
    const correctedValue = this.validateInputValue(value)
    if (value !== correctedValue) {
      value = correctedValue
      this.$emit('input', value)
    }

    this.currentValue = value
  }

  get isDecreaseButtonDisabled() {
    const min = +this.min
    const currentValue = +this.currentValue
    return min === currentValue || currentValue < min || this.disabled
  }

  get isIncreaseButtonDisabled() {
    const max = +this.max
    const currentValue = +this.currentValue
    return max === currentValue || currentValue > max || this.disabled
  }

  @Watch('value')
  onValueChanged(newValue: number | string) {
    if (newValue !== '') {
      newValue = this.validateInputValue(+newValue)
      if (newValue !== this.currentValue) {
        this.currentValue = Number(newValue)
      }
    }
  }

  validateInputValue(value) {
    if (Number.isNaN(value)) {
      value = this.min
    } else {
      value = Math.max(this.min, value)
      value = Math.min(this.max, value)
    }
    return Math.floor(value)
  }

  onInputChange(event) {
    const { value } = event.target
    this.currentValue = value
      ? this.validateInputValue(+value)
      : this.currentValue
    this.emitInputChange()
  }

  doStep(type) {
    if (
      (this.isDecreaseButtonDisabled && type === 'decrease') ||
      (this.isIncreaseButtonDisabled && type === 'increase')
    ) {
      this.$emit('overlimit', type)
      return
    }
    const currentValue = +this.currentValue
    this.currentValue =
      type === 'decrease' ? currentValue - 1 : currentValue + 1
    this.emitInputChange(type)
    this.$emit(type)
  }

  emitInputChange(type?: string) {
    this.$emit('update:modelValue', this.currentValue)
    this.$emit('input', this.currentValue)
    this.$emit('change', this.currentValue)
    if (type) {
      this.$emit('changeType', { type, currentValue: this.currentValue })
    }
  }
}

export default toNative(NumberStepper)
</script>

<style scoped>
.number-stepper {
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  border-radius: 0.3em;
}

.stepper-input {
  text-align: center;
  width: 3em;
  border: none;
  box-shadow: none;
  position: relative;
  background: white;
  font-weight: bold;
}

.stepper-input:active {
  outline: none;
}

.stepper-button {
  position: relative;
  padding: 0.2em 0.6em 0.3em;
  min-width: 2.5em;
  z-index: 1;
  border-radius: 0.3em;
}

.stepper-button span {
  font-size: 2em;
}

.stepper-button.disabled {
  filter: grayscale(1);
  pointer-events: none;
}
</style>
